/**
 * FDI Collections System - Service Layer
 * 
 * Handles HTTP requests to both REST API and AJAX endpoints with
 * retry logic, error handling, and unified response format.
 */

class CollectionService {
    constructor(config = {}) {
        this.config = {
            timeout: 10000,
            retryAttempts: 2,
            retryDelay: 1000,
            ...config
        };
        
        this.requestQueue = new Map();
        this.activeRequests = new Set();
    }

    /**
     * Make HTTP request with retry logic
     * @param {string} url - Request URL
     * @param {Object} options - Fetch options
     * @param {number} attempt - Current attempt number
     * @returns {Promise<Object>} Response data
     */
    async request(url, options = {}, attempt = 1) {
        const requestKey = `${options.method || 'GET'}:${url}`;
        
        /* Prevent duplicate requests */
        if (this.activeRequests.has(requestKey)) {
            return this.requestQueue.get(requestKey);
        }

        const requestPromise = this._makeRequest(url, options, attempt);
        this.activeRequests.add(requestKey);
        this.requestQueue.set(requestKey, requestPromise);

        try {
            const result = await requestPromise;
            return result;
        } finally {
            this.activeRequests.delete(requestKey);
            this.requestQueue.delete(requestKey);
        }
    }

    /**
     * Internal request method with retry logic
     */
    async _makeRequest(url, options, attempt) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

        try {
            const baseHeaders = {
                'X-WP-Nonce': window.iphubCollections?.nonce || '',
            };

            const body = options.body ?? null;
            const isJsonBody = body && !(body instanceof FormData) && !(body instanceof Blob);

            const headers = {
                ...baseHeaders,
                ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
                ...(options.headers || {}),
            };

            const requestOptions = {
                ...options,
                body,
                signal: controller.signal,
                headers,
            };

            const response = await fetch(url, requestOptions);

            clearTimeout(timeoutId);

            /* Handle HTTP errors */
            if (!response.ok) {
                const errorData = await this._parseResponse(response);
                throw new CollectionServiceError(
                    errorData.message || `HTTP ${response.status}`,
                    response.status,
                    errorData
                );
            }

            return await this._parseResponse(response);

        } catch (error) {
            clearTimeout(timeoutId);

            const shouldRetry = this._shouldRetry(error);
            error.retryable = shouldRetry;
            error.attempt = attempt;
            error.maxAttempts = this.config.retryAttempts;

            /* Retry logic for network errors and 5xx responses */
            if (attempt < this.config.retryAttempts && shouldRetry) {
                await this._delay(this.config.retryDelay * attempt);
                return this._makeRequest(url, options, attempt + 1);
            }

            throw error;
        }
    }

    /**
     * Parse response based on content type
     */
    async _parseResponse(response) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
            return await response.json();
        }
        
        return await response.text();
    }

    /**
     * Determine if request should be retried
     */
    _shouldRetry(error) {
        if (error.name === 'AbortError') {
            return false; /* Timeout - don't retry */
        }
        
        if (error instanceof CollectionServiceError) {
            return error.status >= 500; /* Retry 5xx errors */
        }
        
        return true; /* Retry network errors */
    }

    /**
     * Delay execution
     */
    _delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * GET request
     */
    async get(url, params = {}) {
        const urlObj = new URL(url);
        Object.keys(params).forEach(key => {
            if (params[key] !== null && params[key] !== undefined) {
                urlObj.searchParams.append(key, params[key]);
            }
        });

        return this.request(urlObj.toString(), {
            method: 'GET'
        });
    }

    /**
     * POST request
     */
    async post(url, data = {}) {
        return this.request(url, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    /**
     * PUT request
     */
    async put(url, data = {}) {
        return this.request(url, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    /**
     * DELETE request
     */
    async delete(url) {
        return this.request(url, {
            method: 'DELETE'
        });
    }

    /**
     * Make AJAX request to WordPress admin-ajax.php
     */
    /**
     * Make REST API request
     */
    async rest(endpoint, options = {}, params = {}) {
        const baseUrl = window.iphubCollections?.restUrl || '/wp-json/iphub/v1';
        let url = `${baseUrl}${endpoint}`;
        
        // Add query parameters for GET requests
        if (Object.keys(params).length > 0) {
            const urlParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                    urlParams.append(key, params[key]);
                }
            });
            if (urlParams.toString()) {
                url += '?' + urlParams.toString();
            }
        }
        
        return this.request(url, {
            ...options,
            headers: {
                'X-WP-Nonce': window.iphubCollections?.nonce || '',
                ...options.headers
            }
        });
    }

    /**
     * Cancel all pending requests
     */
    cancelAll() {
        this.requestQueue.clear();
        this.activeRequests.clear();
    }

    /**
     * Get request statistics
     */
    getStats() {
        return {
            activeRequests: this.activeRequests.size,
            queuedRequests: this.requestQueue.size
        };
    }
}

/**
 * Custom error class for service errors
 */
class CollectionServiceError extends Error {
    constructor(message, status = 0, data = null) {
        super(message);
        this.name = 'CollectionServiceError';
        this.status = status;
        this.data = data;
    }
}

export { CollectionService };
export default CollectionService;
